import type { ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/copy';
import { env } from '@/lib/env';

/**
 * The developer guide, written for someone who has never seen Tab Service: what the words
 * mean, then the requests in the order a developer makes them, each with the request and
 * the answer. Every example carries this environment's base URL, so what a customer copies
 * from QA runs against QA. The Markdown copy is docs/api-guide.md.
 */
export const GUIDE_SECTIONS = [
  { id: 'overview', title: 'What this is' },
  { id: 'glossary', title: 'Words you will see' },
  { id: 'before-you-start', title: 'Before you start' },
  { id: 'first-efile', title: 'Your first e-file' },
  { id: 'upload', title: 'Send a file instead' },
  { id: 'webhooks', title: 'Webhooks' },
  { id: 'fixes', title: 'When the IRS refuses records' },
  { id: 'errors', title: 'Errors, limits and paging' },
  { id: 'go-live', title: 'Going live' },
] as const;

export function DeveloperGuide() {
  const base = env.apiBaseUrl;
  return (
    <div className="flex min-w-0 flex-col gap-6">
      <Section id="overview" title="What this is">
        <p>
          Tab Service sends your 1099 and other information returns to the IRS for you. You give Tab Service the
          records; Tab Service checks them, sends them to the IRS under Tab Service&apos;s own IRS transmitter number,
          waits for the IRS answer, and shows you the result for every record.
        </p>
        <p>
          You can do all of this from this web application, or from your own software through the API. Both do
          exactly the same things.
        </p>
        <p>
          Base URL for this environment: <Code>{base}</Code>. The examples below already use it. Your live base URL
          is given to you when your account goes live.
        </p>
      </Section>

      <Section id="glossary" title="Words you will see">
        <Table
          head={['Word', 'What it means']}
          rows={[
            ['E-file', 'One batch of records you send to the IRS. Everything starts with an e-file: you create it, follow it, and fix records on it.'],
            ['Record', 'One return inside an e-file, for example one 1099-NEC for one person.'],
            ['Record reference (recordRef)', 'Your own name for a record, such as an account or employee number. You use it later to fix that record. Keep it unique within a form type and tax year.'],
            ['Form type', 'Which IRS form the records are: 1099-NEC, 1099-MISC, 1099-INT and so on.'],
            ['Tax year', 'The year the payments belong to, for example 2026.'],
            ['Transmission', 'One file Tab Service sends to the IRS. An e-file with up to 20,000 records is one transmission; a larger one is split into several. The IRS accepts or rejects each transmission as a whole.'],
            ['Receipt ID', 'The number the IRS gives back for every transmission it received. It is proof of delivery.'],
            ['Correction', 'A new e-file that changes records the IRS has already accepted.'],
            ['Replacement', 'A new e-file that re-sends a transmission the IRS rejected as a whole.'],
            ['API key', 'The secret your software sends with every request so Tab Service knows which account it is.'],
            ['Idempotency key', 'A name you give a request so that sending it twice (after a timeout, say) does not create two e-files.'],
            ['Webhook', 'A web address on your server that Tab Service calls when something happens, so your software does not have to keep asking.'],
          ]}
          plainFirstColumn
        />
      </Section>

      <Section id="before-you-start" title="Before you start">
        <Steps
          items={[
            <>
              <strong>Get an API key.</strong> The owner of your Tab Service account creates it under Settings, then
              API keys. The key is shown once. Store it somewhere safe; if it is lost, revoke it and create another.
            </>,
            <>
              <strong>Test first, then live.</strong> A new account sends everything to the IRS test system. The IRS
              answers for real, but nothing is filed. Once your test e-files look right, Tab Service switches your
              account to live. Test keys start with <Code>tsk_ats_</Code>, live keys with <Code>tsk_live_</Code>. A
              test key can never file for real.
            </>,
          ]}
        />
        <p>
          Every request needs the key in the <Code>X-Api-Key</Code> header. The examples write it as{' '}
          <Code>$KEY</Code>.
        </p>
        <Example
          request={`curl ${base}/v1/me -H "X-Api-Key: $KEY"`}
          response={`{
  "customer": { "id": "3b7ad50f-…", "name": "Acme Payroll LLC", "mode": "ats" },
  "key": { "prefix": "tsk_ats_4f2c", "name": "Payroll system" }
}`}
        />
        <p>
          <Code>mode</Code> is <Code>ats</Code> while you are on the IRS test system and <Code>live</Code> once you
          file for real.
        </p>
      </Section>

      <Section id="first-efile" title="Your first e-file">
        <p>Five requests: see which forms you can file, check your records, send them, wait for the IRS, read the result.</p>

        <h3>1. Which forms and years can I file?</h3>
        <Example
          request={`curl ${base}/v1/forms -H "X-Api-Key: $KEY"`}
          response={`{
  "forms": [
    { "formType": "1099-NEC", "taxYears": [2025, 2026] },
    { "formType": "1099-MISC", "taxYears": [2025, 2026] }
  ]
}`}
        />
        <p>
          To see every field a form takes, and which are required: <Code>GET /v1/forms/1099-NEC/schema</Code>. The
          field names are the same ones Tab Service&apos;s upload templates use, for example{' '}
          <Code>FILER_FEDERAL_ID_NUMBER</Code>, <Code>RECIPIENT_ID_NUMBER</Code>,{' '}
          <Code>BOX1A_NONEMPLOYEE_COMPENSATION</Code>.
        </p>

        <h3>2. Check the records without sending them</h3>
        <p>
          <Code>POST /v1/checks</Code> runs every rule an e-file runs, stores nothing, and tells you what to fix. Use
          it while you build your integration, and before every e-file if you like.
        </p>
        <Example
          request={`curl ${base}/v1/checks -H "X-Api-Key: $KEY" -H "Content-Type: application/json" -d '{
  "formType": "1099-NEC",
  "taxYear": 2026,
  "records": [
    {
      "recordRef": "ACCT0001",
      "FILER_FEDERAL_ID_NUMBER": "123456789",
      "FILER_NAME1": "Acme Payroll LLC",
      "FILER_ADDRESS": "100 Main St",
      "FILER_CITY": "Chicago",
      "FILER_STATE": "IL",
      "FILER_ZIP_CODE": "60601",
      "RECIPIENT_ID_NUMBER": "987654321",
      "RECIPIENT_FIRST_NAME": "Jane",
      "RECIPIENT_LAST_NAME": "Smith",
      "RECIPIENT_ADDRESS": "5 Oak Ave",
      "RECIPIENT_CITY": "Chicago",
      "RECIPIENT_STATE": "IL",
      "RECIPIENT_ZIP_CODE": "60602",
      "BOX1A_NONEMPLOYEE_COMPENSATION": 4500.00
    }
  ]
}'`}
          response={`{ "ok": true, "recordCount": 1, "issues": [] }`}
        />
        <p>When something needs fixing, the answer says which record and which field:</p>
        <CodeBlock label="answer with an issue">{`{
  "ok": false,
  "recordCount": 1,
  "issues": [
    {
      "severity": "error",
      "code": "TIN_INVALID",
      "message": "Recipient TIN must be nine digits.",
      "recordRef": "ACCT0001",
      "field": "RECIPIENT_ID_NUMBER"
    }
  ]
}`}</CodeBlock>
        <p>
          <Code>error</Code> means the record cannot be filed until it is fixed. <Code>warning</Code> means it can be
          filed, but you should look.
        </p>

        <h3>3. Send the records</h3>
        <p>
          The same body as the check, sent to <Code>/v1/e-files</Code>, with an <Code>Idempotency-Key</Code> header.
          Up to 1,000 records can be sent this way in one request; for more, upload a file (next section).
        </p>
        <Example
          request={`curl ${base}/v1/e-files -X POST -H "X-Api-Key: $KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: payroll-2026-batch-7" \\
  -d '{ "formType": "1099-NEC", "taxYear": 2026, "records": [ ... ] }'`}
          response={`{
  "id": "6e90e3ae-8ef6-42ee-9189-b85df37a9887",
  "status": "queued",
  "formType": "1099-NEC",
  "taxYear": 2026,
  "kind": "original",
  "recordCount": 1,
  "transmissionCount": 1,
  "createdAt": "2026-09-06T10:30:43Z"
}`}
        />
        <p>
          The answer comes back at once with <Code>status: queued</Code>. Tab Service sends the e-file to the IRS
          within a minute.
        </p>
        <Note>
          <strong>About the idempotency key.</strong> Pick any name that is unique for this batch on your side: a batch
          number, a date, a job id. If your software sends the same request again with the same key, Tab Service
          returns the e-file it already created instead of filing twice. Tab Service also refuses a file whose content
          it has already filed for you, and records that duplicate ones already accepted for the same form and year.
        </Note>

        <h3>4. Wait for the IRS</h3>
        <Example
          request={`curl ${base}/v1/e-files/6e90e3ae-8ef6-42ee-9189-b85df37a9887 -H "X-Api-Key: $KEY"`}
          response={`{
  "id": "6e90e3ae-…",
  "status": "accepted",
  "recordCount": 1,
  "acceptedCount": 1,
  "errorCount": 0,
  "transmissions": [
    {
      "chunk": 1,
      "status": "accepted",
      "receiptId": "2025-88692436490-b9697f31d",
      "sentAt": "2026-09-06T10:31:12Z",
      "acknowledgedAt": "2026-09-06T10:33:40Z"
    }
  ],
  "timeline": [
    { "at": "2026-09-06T10:30:43Z", "event": "efile.created" },
    { "at": "2026-09-06T10:31:12Z", "event": "transmission.sent", "detail": "chunk 1, receipt 2025-88692436490-b9697f31d" },
    { "at": "2026-09-06T10:33:40Z", "event": "transmission.acknowledged", "detail": "chunk 1: accepted" }
  ]
}`}
        />
        <p>
          The IRS usually answers within a few minutes; it can take longer on a busy day. Tab Service asks the IRS on
          your behalf every few minutes, so you only need to read the e-file until its status stops changing. If you
          would rather be told than ask, set up a webhook (below).
        </p>
        <Table
          head={['Status', 'What it means', 'What to do']}
          rows={[
            ['queued', 'Tab Service has it and will send it within a minute.', 'Nothing.'],
            ['transmitting', 'It is being sent to the IRS right now.', 'Nothing.'],
            ['processing', 'The IRS has it and has not answered yet.', 'Wait.'],
            ['accepted', 'The IRS accepted every record.', 'Done.'],
            ['accepted_with_errors', 'The IRS accepted the e-file but flagged some records.', 'Read the flagged records and send corrections.'],
            ['partially_accepted', 'Some transmissions were accepted, some rejected.', 'Correct the accepted ones if needed, replace the rejected ones.'],
            ['rejected', 'The IRS rejected the whole transmission.', 'Read the reason and send a replacement.'],
            ['failed', 'Tab Service could not send it.', 'Read the reason; Tab Service support is alerted.'],
          ]}
        />

        <h3>5. Read the result for each record</h3>
        <Example
          request={`curl "${base}/v1/e-files/6e90e3ae-…/records?status=error" -H "X-Api-Key: $KEY"`}
          response={`{
  "items": [
    {
      "recordRef": "ACCT0002",
      "status": "error",
      "chunk": 1,
      "errors": [
        { "code": "1099NEC-014", "message": "RecipientTIN and RecipientName do not match IRS records.", "field": "RecipientTIN" }
      ],
      "fix": { "state": "none" }
    }
  ],
  "nextCursor": null
}`}
        />
        <p>
          A record&apos;s <Code>status</Code> is <Code>pending</Code> (not sent yet), <Code>transmitted</Code> (sent,
          no answer yet), <Code>accepted</Code>, <Code>error</Code> (accepted with an IRS error on this record) or{' '}
          <Code>rejected</Code> (in a rejected transmission). Add <Code>fixState=attention</Code> to list only the
          records that still need a decision.
        </p>
      </Section>

      <Section id="upload" title="Send a file instead of JSON">
        <p>
          If your system already produces a file, or you have more than 1,000 records, upload the file. Tab Service
          reads CSV, Excel, IRS Publication 1220 (the &quot;FIRE&quot; format) and IRIS XML.
        </p>
        <Steps
          items={[
            <>
              <strong>Ask for an upload slot.</strong>
              <Example
                request={`curl ${base}/v1/uploads -X POST -H "X-Api-Key: $KEY" -H "Content-Type: application/json" -d '{
  "fileName": "1099-nec-2026.csv",
  "format": "csv",
  "formType": "1099-NEC",
  "taxYear": 2026,
  "sizeBytes": 8421904
}'`}
                response={`{
  "id": "0f14336a-…",
  "status": "awaiting_file",
  "uploadUrl": "https://…blob.core.windows.net/…?sv=…",
  "uploadUrlExpiresAt": "2026-09-06T11:00:00Z"
}`}
              />
              <p>
                <Code>format</Code> is <Code>csv</Code>, <Code>xlsx</Code>, <Code>pub1220</Code> or{' '}
                <Code>irisxml</Code>. <Code>formType</Code> is needed for CSV and Excel; 1220 and XML files say which
                form they hold.
              </p>
            </>,
            <>
              <strong>Put the file at that address.</strong> It goes straight to storage, not through the API, so a
              large file does not tie up your request.
              <CodeBlock label="request">{`curl -X PUT "$UPLOAD_URL" -H "x-ms-blob-type: BlockBlob" -H "Content-Type: text/plain" --data-binary @1099-nec-2026.csv`}</CodeBlock>
            </>,
            <>
              <strong>Wait until it has been checked.</strong> Tab Service reads the file and runs the same checks as{' '}
              <Code>POST /v1/checks</Code>. A 60,000-record file is checked in under a minute.
              <Example
                request={`curl ${base}/v1/uploads/0f14336a-… -H "X-Api-Key: $KEY"`}
                response={`{ "id": "0f14336a-…", "status": "checked", "recordCount": 60000, "issues": [] }`}
              />
              <p>
                <Code>checked</Code> means it can be filed. <Code>blocked</Code> means something must be fixed;{' '}
                <Code>issues</Code> says what, record by record.
              </p>
            </>,
            <>
              <strong>File it.</strong>
              <CodeBlock label="request">{`curl ${base}/v1/e-files -X POST -H "X-Api-Key: $KEY" -H "Content-Type: application/json" \\
  -H "Idempotency-Key: 1099-nec-2026-upload" -d '{ "uploadId": "0f14336a-…" }'`}</CodeBlock>
              <p>
                The e-file is created at once and answers <Code>202</Code> with <Code>status: queued</Code>. Its
                records are written in the background: the transmissions appear on the e-file within seconds for a
                small file and over a minute or two for 60,000 records, one per 20,000 records, and are sent as they
                are written.
              </p>
            </>,
          ]}
        />
        <p>
          <strong>CSV and Excel</strong> use the column names from Tab Service&apos;s templates; common variations are
          recognised. <strong>IRS 1220</strong> files are read exactly as your system writes them.{' '}
          <strong>IRIS XML</strong> is checked against the IRS schema and sent as you supplied it, under Tab
          Service&apos;s transmitter header.
        </p>
      </Section>

      <Section id="webhooks" title="Be told when something happens: webhooks">
        <p>
          Instead of reading an e-file every minute until the IRS answers, give Tab Service a web address on your
          server. Tab Service sends an HTTP POST to it whenever:
        </p>
        <Table
          head={['Event', 'When']}
          rows={[
            ['upload.checked', 'A file you uploaded has been checked (or blocked).'],
            ['efile.transmitted', 'A transmission has been sent to the IRS and has a receipt ID.'],
            ['efile.acknowledged', 'The IRS has answered for a transmission.'],
            ['efile.failed', 'Tab Service could not send an e-file.'],
          ]}
        />
        <p>
          <strong>Set it up:</strong> Settings, then Webhooks, then Add endpoint. Enter an HTTPS address on your
          server and choose which events you want (or all of them). Tab Service shows you a secret once; store it.
          Use Send test to see a delivery arrive.
        </p>
        <p>
          <strong>What arrives</strong> is a POST with a JSON body and three headers:
        </p>
        <CodeBlock label="delivery">{`POST /tab-service/events HTTP/1.1
Content-Type: application/json
X-TabService-Event: efile.acknowledged
X-TabService-Delivery: 7c1f3e2a-…
X-TabService-Signature: t=1757000000,v1=9c4b…e1

{
  "id": "7c1f3e2a-…",
  "event": "efile.acknowledged",
  "createdAt": "2026-09-06T10:33:40Z",
  "data": {
    "efileId": "6e90e3ae-…",
    "chunk": 1,
    "receiptId": "2025-88692436490-b9697f31d",
    "chunkStatus": "accepted",
    "efileStatus": "accepted",
    "errorCount": 0
  }
}`}</CodeBlock>
        <p>
          <strong>Check that it really came from Tab Service.</strong> The signature header carries the time the
          message was signed (<Code>t</Code>) and an HMAC-SHA256 (<Code>v1</Code>) of{' '}
          <Code>{'<t>.<raw body>'}</Code> made with your secret. Compute the same value and compare:
        </p>
        <CodeBlock label="Node.js">{`import { createHmac, timingSafeEqual } from "node:crypto";

function verify(secret, header, rawBody) {
  const parts = Object.fromEntries(header.split(",").map((p) => p.split("=")));
  const expected = createHmac("sha256", secret).update(\`\${parts.t}.\${rawBody}\`).digest("hex");
  const fresh = Math.abs(Date.now() / 1000 - Number(parts.t)) < 300; // five minutes
  return fresh && timingSafeEqual(Buffer.from(expected), Buffer.from(parts.v1));
}`}</CodeBlock>
        <p>
          Answer with any 2xx status within a few seconds. If your server does not answer, Tab Service tries again
          later, several times, with growing gaps. An endpoint that keeps failing is marked failing and then
          disabled; it stays in the list so you can see it and fix it. The delivery history is under Settings, then
          Webhooks.
        </p>
      </Section>

      <Section id="fixes" title="When the IRS refuses records">
        <p>
          There are two different situations, and the IRS handles them differently. Tab Service works out which one
          applies; you only supply the right data.
        </p>
        <ul>
          <li>
            <strong>The IRS accepted the e-file but flagged some records</strong> (<Code>accepted_with_errors</Code>),
            or you spotted a mistake in records the IRS accepted. Send a <strong>correction</strong>: a new e-file
            that changes those records.
          </li>
          <li>
            <strong>The IRS rejected a whole transmission</strong> (<Code>rejected</Code> or{' '}
            <Code>partially_accepted</Code>). Nothing in it was filed. Send a <strong>replacement</strong>: the fixed
            records of that transmission, re-sent against its receipt ID, within the IRS grace period.
          </li>
        </ul>
        <h3>Fix a record and send the fix</h3>
        <Steps
          items={[
            <>
              <strong>Save the fixed data on the record.</strong> Tab Service checks it and queues it; the answer says
              whether it will go as a correction or a replacement.
              <Example
                request={`curl ${base}/v1/e-files/6e90e3ae-…/records/ACCT0002 -X PATCH -H "X-Api-Key: $KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "canonical": { "recordRef": "ACCT0002", "RECIPIENT_ID_NUMBER": "987654322", ...every field of the record... } }'`}
                response={`{ "recordRef": "ACCT0002", "status": "error", "fix": { "state": "queued", "queuedFor": "correction" } }`}
              />
            </>,
            <>
              <strong>Send every queued fix as one new e-file.</strong> For rejected transmissions use{' '}
              <Code>/replacements</Code> the same way. Either answer is a new e-file (<Code>kind: correction</Code> or{' '}
              <Code>kind: replacement</Code>) that you follow like any other.
              <CodeBlock label="request">{`curl ${base}/v1/e-files/6e90e3ae-…/corrections -X POST -H "X-Api-Key: $KEY" \\
  -H "Content-Type: application/json" -H "Idempotency-Key: corrections-batch-7-1" -d '{ "fromQueued": true }'`}</CodeBlock>
            </>,
            <>
              <strong>Watch it settle.</strong> When the IRS accepts the new e-file, the original records show{' '}
              <Code>fix.state: corrected</Code>. If the IRS refuses the fix, they show <Code>fix_failed</Code> and
              need another look.
            </>,
          ]}
        />
        <p>
          You can also build the correction or replacement yourself and send it inline (<Code>records</Code> in the
          body) or as an upload; Tab Service reads the IRS references from your data or from its own history of the
          e-file. Each rejected transmission can be replaced once; a partially rejected e-file is replaced
          transmission by transmission, in as many replacement e-files as you need.
        </p>
      </Section>

      <Section id="errors" title="Errors, limits and paging">
        <p>
          Every error is a JSON body with a <Code>title</Code>, an HTTP <Code>status</Code> and a <Code>detail</Code>{' '}
          written for a person. A <Code>422</Code> from a check or an e-file also lists the issues, record by record.
        </p>
        <CodeBlock label="error body">{`{
  "type": "about:blank",
  "title": "Unprocessable Entity",
  "status": 422,
  "detail": "2 records were refused by the checks.",
  "issues": [ { "severity": "error", "code": "TIN_INVALID", "recordRef": "ACCT0001", "field": "RECIPIENT_ID_NUMBER", "message": "Recipient TIN must be nine digits." } ]
}`}</CodeBlock>
        <Table
          head={['Status', 'Meaning']}
          rows={[
            ['400', 'The request is malformed or a required field is missing.'],
            ['401', 'No key, or a key that was revoked.'],
            ['403', 'The key may not do this: a form that is not enabled for the account, or something only the account owner can do.'],
            ['404', 'Nothing with that id belongs to your account.'],
            ['409', 'The action does not fit the current state, for example a fix that has already been sent.'],
            ['413', 'More than 1,000 records in one request. Upload a file instead.'],
            ['422', 'The records did not pass the checks. The issues are listed.'],
            ['429', 'Too many requests. Wait the number of seconds in Retry-After.'],
          ]}
        />
        <p>
          Each key may make 120 requests a minute. Lists are paged two ways. A program walking a whole list passes{' '}
          <Code>limit</Code> (up to 500) and, for the next page, the <Code>cursor</Code> value returned as{' '}
          <Code>nextCursor</Code>. A screen showing page numbers passes <Code>page</Code> instead (
          <Code>page=12&amp;limit=10</Code>) and gets <Code>total</Code> and <Code>pageSize</Code> back with the rows. <Code>GET /v1/e-files</Code>{' '}
          also takes <Code>status</Code>, <Code>formType</Code>, <Code>taxYear</Code> and <Code>q</Code> (free text),
          and its first page carries <Code>statusCounts</Code> for the year.
        </p>
      </Section>

      <Section id="go-live" title="Going live: a checklist">
        <Steps
          items={[
            'Your test e-files come back accepted from the IRS test system.',
            'Your software stores the e-file id and recordRef values, so it can find records later.',
            'Your software sends an Idempotency-Key with every e-file and every correction.',
            'If you use webhooks, your endpoint verifies the signature and answers within a few seconds.',
            'Ask Tab Service to switch the account to live, then create a live key. The test key keeps working against the IRS test system.',
          ]}
        />
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <Card id={id} className="scroll-mt-20">
      <h2 className="mb-4 text-xl font-semibold text-primary">{title}</h2>
      <div className="flex max-w-[76ch] flex-col gap-4 text-base leading-relaxed text-gray-3 [&_h3]:mt-3 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-3 [&_strong]:font-medium [&_strong]:text-gray-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-2">
        {children}
      </div>
    </Card>
  );
}

function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="flex list-decimal flex-col gap-3 pl-6 marker:font-medium marker:text-primary">
      {items.map((item, index) => (
        <li key={index} className="flex flex-col gap-3 pl-1">
          {item}
        </li>
      ))}
    </ol>
  );
}

function Note({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border-l-4 border-primary bg-[color:var(--ts-blue-bglayout)] px-4 py-3 text-base text-gray-3">
      {children}
    </div>
  );
}

function Code({ children }: { children: ReactNode }) {
  return <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.9em] text-gray-3">{children}</code>;
}

/** A request and the answer it gets, one under the other, each copyable. */
function Example({ request, response }: { request: string; response: string }) {
  return (
    <div className="flex flex-col gap-2">
      <CodeBlock label="request">{request}</CodeBlock>
      <CodeBlock label="answer">{response}</CodeBlock>
    </div>
  );
}

function CodeBlock({ children, label }: { children: string; label?: string }) {
  return (
    <div className="relative min-w-0">
      {label ? (
        <span className="absolute top-2 left-3 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">{label}</span>
      ) : null}
      <pre
        className={
          'overflow-x-auto rounded-lg border border-[color:var(--ts-border-soft)] bg-[color:var(--ts-code-bg,var(--ts-blue-bglayout))] p-4 pr-12 font-mono text-sm leading-relaxed text-gray-3 ' +
          (label ? 'pt-7' : '')
        }
      >
        {children}
      </pre>
      <div className="absolute top-2 right-2">
        <CopyButton value={children} label={label ?? 'example'} />
      </div>
    </div>
  );
}

function Table({ head, rows, plainFirstColumn }: { head: string[]; rows: string[][]; plainFirstColumn?: boolean }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[color:var(--ts-border-soft)]">
      <table className="w-full text-base">
        <thead className="bg-[color:var(--ts-table-head-bg,var(--ts-blue-bglayout))] text-left text-sm text-muted-foreground">
          <tr>
            {head.map((cell) => (
              <th key={cell} className="px-3 py-2 font-medium">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[color:var(--ts-border-soft)] align-top">
          {rows.map((row) => (
            <tr key={row[0]}>
              {row.map((cell, index) => (
                <td
                  key={cell}
                  className={
                    index === 0
                      ? plainFirstColumn
                        ? 'px-3 py-2 font-medium whitespace-nowrap text-gray-3'
                        : 'px-3 py-2 font-mono text-sm whitespace-nowrap'
                      : 'px-3 py-2'
                  }
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
